import * as v from 'valibot';
export const ACP_INTERACTION_PROTOCOL_VERSION = 1;
export const ACP_INTERACTION_CAPABILITY_VERSION = 1;
export const ACP_INTERACTION_ATTENTION_SOURCE = 'acp_interaction';
export const ACP_INTERACTION_KIND_VALUES = ['permission', 'form', 'url'];
export const ACP_INTERACTION_STATE_VALUES = [
    'pending',
    'answered',
    'delivery_confirmed',
    'delivery_unconfirmed',
    'interrupted',
    'expired',
    'cancelled',
];
export const ACP_INTERACTION_DECISION_KIND_VALUES = [
    'selected_option',
    'accepted',
    'declined',
    'cancelled',
];
export const ACP_INTERACTION_DELIVERY_OUTCOME_VALUES = [
    'consumed',
    'duplicate',
    'stale_generation',
    'no_waiter',
    'conflict',
    'unknown',
];
export const ACP_RUNTIME_ANSWER_STATUS_VALUES = [
    'consumed',
    'duplicate',
    'stale_generation',
    'no_waiter',
    'conflict',
];
/**
 * Creating reviewed permission requests is off until a deployment opts in after verifying its agent
 * runtimes are compatible. Pending requests stay answerable while creation is off. Forms and URL
 * elicitations have their own opt-in switches below.
 */
export const DEFAULT_ACP_INTERACTIONS_ENABLED = false;
export const DEFAULT_ACP_INTERACTION_FORMS_ENABLED = false;
export const DEFAULT_ACP_INTERACTION_URLS_ENABLED = false;
export const DEFAULT_ACP_INTERACTION_URL_DEADLINE_MS = 10 * 60 * 1000;
export const DEFAULT_ACP_INTERACTION_URL_MAX_CHARS = 8192;
export const DEFAULT_ACP_INTERACTION_URL_ELICITATION_ID_MAX_CHARS = 256;
export const DEFAULT_ACP_INTERACTION_URL_REDIRECT_DEPTH = 2;
export const DEFAULT_ACP_INTERACTION_PERMISSION_CONVERSATION_DEADLINE_MS = 2 * 60 * 60 * 1000;
export const DEFAULT_ACP_INTERACTION_PERMISSION_TASK_DEADLINE_MS = 30 * 60 * 1000;
export const DEFAULT_ACP_INTERACTION_MAX_DEADLINE_MS = 4 * 60 * 60 * 1000;
export const DEFAULT_ACP_INTERACTION_DEADLINE_MARGIN_MS = 60 * 1000;
export const DEFAULT_ACP_INTERACTION_MAX_PENDING_PER_SESSION = 8;
export const DEFAULT_ACP_INTERACTION_REQUEST_MAX_BYTES = 32 * 1024;
export const DEFAULT_ACP_INTERACTION_OPTIONS_MAX_COUNT = 16;
export const DEFAULT_ACP_INTERACTION_OPTION_ID_MAX_CHARS = 128;
export const DEFAULT_ACP_INTERACTION_OPTION_NAME_MAX_CHARS = 200;
export const DEFAULT_ACP_INTERACTION_FORM_SCHEMA_MAX_BYTES = 16 * 1024;
export const DEFAULT_ACP_INTERACTION_FORM_SCHEMA_MAX_PROPERTIES = 20;
export const DEFAULT_ACP_INTERACTION_FORM_SCHEMA_MAX_ENUM = 50;
export const DEFAULT_ACP_INTERACTION_ANSWER_MAX_BYTES = 16 * 1024;
export const DEFAULT_ACP_INTERACTION_ANSWER_STRING_MAX_BYTES = 4 * 1024;
export const DEFAULT_ACP_INTERACTION_RETRY_DELAYS_MS = [1000, 5000, 30000, 120000, 300000];
export const DEFAULT_ACP_INTERACTION_RETRY_STEADY_MS = 300000;
export const DEFAULT_ACP_INTERACTION_DELIVERY_WINDOW_MS = 15 * 60 * 1000;
export const DEFAULT_ACP_INTERACTION_SENSITIVE_PURGE_MS = 60 * 60 * 1000;
export const DEFAULT_ACP_INTERACTION_SUMMARY_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
export const DEFAULT_ACP_INTERACTION_SUMMARY_LAST_SETTLED = 100;
export const DEFAULT_ACP_INTERACTION_SNAPSHOT_LAST_SETTLED = 20;
export const DEFAULT_ACP_INTERACTION_EXPIRY_BATCH_SIZE = 25;
export const DEFAULT_ACP_INTERACTION_OUTBOX_BATCH_SIZE = 25;
export const DEFAULT_ACP_INTERACTION_DELIVERY_BATCH_SIZE = 1;
export const DEFAULT_ACP_INTERACTION_ALARM_WALL_TIME_MS = 15_000;
export const DEFAULT_ACP_INTERACTION_ALARM_REARM_DELAY_MS = 1_000;
export const DEFAULT_ACP_INTERACTION_RUNTIME_RECEIPT_LIMIT = 256;
export const DEFAULT_ACP_INTERACTION_RUNTIME_RESPONSE_MAX_BYTES = 64 * 1024;
export const AcpInteractionIdSchema = v.pipe(v.string(), v.uuid());
export const AcpInteractionGenerationSchema = v.pipe(v.string(), v.uuid());
export const AcpInteractionHashSchema = v.pipe(v.string(), v.regex(/^[a-f0-9]{64}$/));
export const AcpInteractionKeySchema = v.pipe(v.string(), v.minLength(1), v.maxLength(128));
export const AcpInteractionKindSchema = v.picklist(ACP_INTERACTION_KIND_VALUES);
export const AcpInteractionStateSchema = v.picklist(ACP_INTERACTION_STATE_VALUES);
export const AcpInteractionDecisionKindSchema = v.picklist(ACP_INTERACTION_DECISION_KIND_VALUES);
export const AcpRuntimeAnswerStatusSchema = v.picklist(ACP_RUNTIME_ANSWER_STATUS_VALUES);
export const AcpInteractionOptionSchema = v.object({
    id: v.pipe(v.string(), v.minLength(1), v.maxLength(128)),
    kind: v.picklist(['allow_once', 'allow_always', 'reject_once', 'reject_always', 'custom']),
    name: v.pipe(v.string(), v.minLength(1), v.maxLength(DEFAULT_ACP_INTERACTION_OPTION_NAME_MAX_CHARS)),
});
export const AcpInteractionSafeSummarySchema = v.object({
    interactionId: AcpInteractionIdSchema,
    kind: AcpInteractionKindSchema,
    state: AcpInteractionStateSchema,
    createdAt: v.number(),
    updatedAt: v.number(),
    deadlineAt: v.number(),
    answeredAt: v.nullable(v.number()),
    deliveryState: v.nullable(v.picklist(['pending', 'confirmed', 'unconfirmed', 'interrupted'])),
    attentionMarkerId: v.nullable(v.string()),
    toolCallId: v.nullable(v.string()),
    urlCompletedAt: v.optional(v.nullable(v.number())),
});
export const AcpInteractionEncryptedPayloadSchema = v.object({
    ciphertext: v.string(),
    iv: v.string(),
});
export const AcpInteractionRuntimeCreateSchema = v.object({
    protocolVersion: v.literal(ACP_INTERACTION_PROTOCOL_VERSION),
    interactionId: AcpInteractionIdSchema,
    generation: AcpInteractionGenerationSchema,
    runtimeIdentity: v.pipe(v.string(), v.minLength(1), v.maxLength(512)),
    agentSessionId: v.pipe(v.string(), v.minLength(1), v.maxLength(256)),
    kind: AcpInteractionKindSchema,
    payloadHash: AcpInteractionHashSchema,
    detail: v.unknown(),
    safeSummary: v.object({
        toolCallId: v.optional(v.nullable(v.pipe(v.string(), v.maxLength(256)))),
        optionCount: v.optional(v.number()),
    }),
    deadlineAt: v.number(),
    upstreamRequestId: v.optional(v.nullable(v.pipe(v.string(), v.maxLength(256)))),
});
export const AcpInteractionRuntimeSettleSchema = v.object({
    protocolVersion: v.literal(ACP_INTERACTION_PROTOCOL_VERSION),
    interactionId: AcpInteractionIdSchema,
    generation: AcpInteractionGenerationSchema,
    runtimeIdentity: v.pipe(v.string(), v.minLength(1), v.maxLength(512)),
    agentSessionId: v.pipe(v.string(), v.minLength(1), v.maxLength(256)),
    reason: v.picklist([
        'wrapper_cancelled',
        'connection_closed',
        'connection_replaced',
        'session_stopped',
        'expired',
        'completed',
    ]),
});
export const AcpInteractionRuntimeCompleteUrlSchema = v.object({
    protocolVersion: v.literal(ACP_INTERACTION_PROTOCOL_VERSION),
    interactionId: AcpInteractionIdSchema,
    generation: AcpInteractionGenerationSchema,
    runtimeIdentity: v.pipe(v.string(), v.minLength(1), v.maxLength(512)),
    agentSessionId: v.pipe(v.string(), v.minLength(1), v.maxLength(256)),
    elicitationId: v.pipe(v.string(), v.minLength(1), v.maxLength(256)),
});
export const AcpInteractionAnswerDecisionSchema = v.object({
    kind: AcpInteractionDecisionKindSchema,
    optionId: v.optional(v.string()),
    encryptedAnswer: v.optional(AcpInteractionEncryptedPayloadSchema),
    content: v.optional(v.record(v.string(), v.unknown())),
    answerHash: AcpInteractionHashSchema,
});
export const AcpInteractionBrowserAnswerSchema = v.object({
    answerKey: AcpInteractionKeySchema,
    decision: AcpInteractionAnswerDecisionSchema,
});
export const AcpRuntimeAnswerRequestSchema = v.object({
    protocolVersion: v.literal(ACP_INTERACTION_PROTOCOL_VERSION),
    interactionId: AcpInteractionIdSchema,
    generation: AcpInteractionGenerationSchema,
    runtimeIdentity: v.pipe(v.string(), v.minLength(1), v.maxLength(512)),
    decision: AcpInteractionAnswerDecisionSchema,
});
export const AcpRuntimeAnswerResponseSchema = v.object({
    status: AcpRuntimeAnswerStatusSchema,
    interactionId: AcpInteractionIdSchema,
    generation: AcpInteractionGenerationSchema,
    runtimeIdentity: v.string(),
});
export const AcpInteractionRuntimeConfigSchema = v.object({
    enabled: v.boolean(),
    formsEnabled: v.optional(v.boolean()),
    protocolVersion: v.literal(ACP_INTERACTION_PROTOCOL_VERSION),
    permissionDeadlineMs: v.pipe(v.number(), v.integer(), v.minValue(1)),
    formDeadlineMs: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
    urlsEnabled: v.optional(v.boolean()),
    urlDeadlineMs: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
    urlMaxChars: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
    urlElicitationIdMaxChars: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
    urlRedirectDepth: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0))),
    maxDeadlineMs: v.pipe(v.number(), v.integer(), v.minValue(1)),
    deadlineMarginMs: v.pipe(v.number(), v.integer(), v.minValue(0)),
    requestMaxBytes: v.pipe(v.number(), v.integer(), v.minValue(1)),
    optionsMaxCount: v.pipe(v.number(), v.integer(), v.minValue(1)),
    optionIdMaxChars: v.pipe(v.number(), v.integer(), v.minValue(1)),
    optionNameMaxChars: v.pipe(v.number(), v.integer(), v.minValue(1)),
    receiptLimit: v.pipe(v.number(), v.integer(), v.minValue(1)),
    responseMaxBytes: v.pipe(v.number(), v.integer(), v.minValue(1)),
    formSchemaMaxBytes: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
    formSchemaMaxProperties: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
    formSchemaMaxEnum: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
    answerMaxBytes: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
    answerStringMaxBytes: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1))),
    settleRetryDelaysMs: v.array(v.pipe(v.number(), v.integer(), v.minValue(1))),
    settleRetrySteadyMs: v.pipe(v.number(), v.integer(), v.minValue(1)),
});
export function buildAcpInteractionAnswerPath(workspaceId, agentSessionId, interactionId) {
    return `/workspaces/${encodeURIComponent(workspaceId)}/agent-sessions/${encodeURIComponent(agentSessionId)}/interactions/${encodeURIComponent(interactionId)}/answer`;
}
//# sourceMappingURL=acp-interactions.js.map